/**
 * components/ExcelExportProgress/ExcelExportProgress.js
 * Composant de progression pour l'export Excel
 */

export class ExcelExportProgress {
  constructor() {
    /** @type {HTMLDivElement|null} */
    this.overlay = null;
    this.currentPercent = 0;
  }

  /**
   * Afficher la barre de progression
   */
  show() {
    if (this.overlay) return; // Déjà affiché

    this.overlay = document.createElement("div");
    this.overlay.className = "excel-export-progress-overlay";
    this.overlay.innerHTML = `
      <div class="excel-export-progress-container">
        <div class="excel-export-progress-header">
          <div class="excel-export-progress-icon spinning">
            📊
          </div>
          <h3 class="excel-export-progress-title">Export Excel en cours</h3>
        </div>
        
        <div class="excel-export-progress-content">
          <div class="excel-export-progress-message">
            Initialisation...
          </div>
          
          <div class="excel-export-progress-stats">
            <span class="excel-export-progress-label">Progression</span>
            <span class="excel-export-progress-percentage">0%</span>
          </div>
          
          <div class="excel-export-progress-bar-container">
            <div class="excel-export-progress-bar indeterminate"></div>
          </div>
        </div>
        
        <div class="excel-export-progress-details">
          <div class="excel-export-progress-detail-item">
            <span class="excel-export-progress-detail-icon">⏱️</span>
            <span class="excel-export-progress-detail-text">En cours...</span>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlay);
    this.currentPercent = 0;
  }

  /**
   * Mettre à jour la progression
   * @param {string} message - Message de progression
   * @param {number|null} percentage - Pourcentage (optionnel)
   */
  update(message, percentage = null) {
    if (!this.overlay) return;

    const messageEl = this.overlay.querySelector(".excel-export-progress-message");
    const percentEl = this.overlay.querySelector(".excel-export-progress-percentage");
    const barEl = this.overlay.querySelector(".excel-export-progress-bar");
    const detailEl = this.overlay.querySelector(".excel-export-progress-detail-text");

    // Mettre à jour le message
    if (messageEl) {
      messageEl.textContent = message;
    }

    // Mettre à jour le pourcentage et la barre
    if (percentage !== null && percentage >= 0 && percentage <= 100) {
      this.currentPercent = percentage;

      if (percentEl) {
        percentEl.textContent = `${percentage}%`;
      }

      if (barEl) {
        barEl.classList.remove("indeterminate");
        /** @type {HTMLElement} */ (barEl).style.width = `${percentage}%`;
      }
    } else {
      // Mode indéterminé si pas de pourcentage
      if (barEl && !barEl.classList.contains("indeterminate")) {
        barEl.classList.add("indeterminate");
      }
    }

    // Mettre à jour le détail
    if (detailEl) {
      detailEl.textContent = this._getEstimatedTime(percentage);
    }
  }

  /**
   * Marquer comme complété
   * @param {string} message - Message de succès
   */
  complete(message = "Export terminé avec succès") {
    if (!this.overlay) return;

    const container = this.overlay.querySelector(".excel-export-progress-container");
    const icon = this.overlay.querySelector(".excel-export-progress-icon");
    const messageEl = this.overlay.querySelector(".excel-export-progress-message");
    const percentEl = this.overlay.querySelector(".excel-export-progress-percentage");
    const barEl = this.overlay.querySelector(".excel-export-progress-bar");

    if (container) {
      container.classList.add("completed");
    }

    if (icon) {
      icon.classList.remove("spinning");
      icon.textContent = "✅";
    }

    if (messageEl) {
      messageEl.textContent = message;
    }

    if (percentEl) {
      percentEl.textContent = "100%";
    }

    if (barEl) {
      barEl.classList.remove("indeterminate");
      /** @type {HTMLElement} */ (barEl).style.width = "100%";
    }

    // Masquer automatiquement après 2 secondes
    setTimeout(() => {
      this.hide();
    }, 2000);
  }

  /**
   * Marquer comme erreur
   * @param {string} message - Message d'erreur
   */
  error(message = "Une erreur est survenue") {
    if (!this.overlay) return;

    const container = this.overlay.querySelector(".excel-export-progress-container");
    const icon = this.overlay.querySelector(".excel-export-progress-icon");
    const messageEl = this.overlay.querySelector(".excel-export-progress-message");

    if (container) {
      container.classList.add("error");
    }

    if (icon) {
      icon.classList.remove("spinning");
      icon.textContent = "❌";
    }

    if (messageEl) {
      messageEl.textContent = message;
    }

    // Masquer automatiquement après 3 secondes
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  /**
   * Masquer la barre de progression
   */
  hide() {
    if (this.overlay) {
      this.overlay.style.opacity = "0";
      setTimeout(() => {
        if (this.overlay && this.overlay.parentNode) {
          this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.currentPercent = 0;
      }, 200);
    }
  }

  /**
   * Calculer le temps estimé restant
   * @private
   */
  _getEstimatedTime(percentage) {
    if (percentage === null || percentage === 0) {
      return "Calcul en cours...";
    }
    if (percentage >= 95) {
      return "Presque terminé...";
    }
    if (percentage >= 50) {
      return "Quelques secondes restantes...";
    }
    return "En cours...";
  }

  /**
   * Extraire le pourcentage d'un message
   * @param {string} message - Message de progression
   * @returns {number|null} Pourcentage ou null
   */
  static extractPercentage(message) {
    const match = message.match(/\((\d+)%\)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    return null;
  }

  /**
   * Mapper un message à un pourcentage estimé
   * @param {string} message - Message de progression
   * @returns {number|null} Pourcentage estimé
   */
  static mapMessageToPercentage(message) {
    // Si le message contient déjà un pourcentage, l'utiliser
    const extracted = this.extractPercentage(message);
    if (extracted !== null) {
      return extracted;
    }

    // Sinon, estimer basé sur le message
    if (message.includes("Vérification")) return 5;
    if (message.includes("Préparation")) return 10;
    if (message.includes("Connexion")) return 15;
    if (message.includes("Enrichissement")) return 50;
    if (message.includes("bordures")) return 85;
    if (message.includes("Sauvegarde") || message.includes("Saving")) return 90;
    if (message.includes("Téléchargement")) return 95;
    if (message.includes("terminé") || message.includes("succès")) return 100;

    return null; // Mode indéterminé
  }
}
